/**
 * Per-machine price and cost, read from the engine's `perSystem` breakdown.
 *
 * The engine attributes every welding line except the project services to the
 * machine type that generated it, so these rows sum to the welding total less
 * services. Nothing is recomputed here; this only shapes and ranks what the
 * engine returned, and aggregates it across spreads.
 */
import { MACHINE_LABELS, MachineKey, MACHINE_KEYS, ProjectState } from './spreadSplitData';
import { SpreadPriceState } from './useSpreadPrice';

export interface MachineLine {
  key: string;
  label: string;
  units: number;
  actualUnits: number;
  spareUnits: number;
  /** Purchase cost of one machine. */
  unitCost: number;
  /** Rental charged per machine per month, after any duration discount. */
  monthlyRentalPerUnit: number;
  hardwarePrice: number;
  softwarePrice: number;
  connectivityPrice: number;
  mobilizationPrice: number;
  totalPrice: number;
  /** One-time purchase cost of these machines (COGS). */
  hardwareCost: number;
}

const num = (v: unknown): number => (typeof v === 'number' ? v : 0);
const isMachineKey = (k: string): k is MachineKey => (MACHINE_KEYS as string[]).includes(k);
const labelFor = (key: string): string => (isMachineKey(key) ? MACHINE_LABELS[key] : key.toUpperCase());

/** Machine rows for one spread's welding result, biggest price contribution first. */
export function machineLines(details: Record<string, unknown> | undefined): MachineLine[] {
  const perSystem = details?.perSystem as Record<string, Record<string, unknown>> | undefined;
  if (!perSystem) return [];
  return Object.entries(perSystem)
    .map(([key, r]) => ({
      key,
      label: labelFor(key),
      units: num(r.units),
      actualUnits: num(r.actualUnits),
      spareUnits: num(r.spareUnits),
      unitCost: num(r.unitCost),
      monthlyRentalPerUnit: num(r.monthlyRentalPerUnit),
      hardwarePrice: num(r.hardwarePrice),
      softwarePrice: num(r.softwarePrice),
      connectivityPrice: num(r.connectivityPrice),
      mobilizationPrice: num(r.mobilizationPrice),
      totalPrice: num(r.totalPrice),
      hardwareCost: num(r.hardwareCost),
    }))
    .filter((row) => row.units > 0)
    .sort((a, b) => b.totalPrice - a.totalPrice);
}

const ADDITIVE: (keyof MachineLine)[] = [
  'units', 'actualUnits', 'spareUnits', 'hardwarePrice', 'softwarePrice',
  'connectivityPrice', 'mobilizationPrice', 'totalPrice', 'hardwareCost',
];

function merge(into: MachineLine, from: MachineLine): MachineLine {
  const merged = { ...into };
  ADDITIVE.forEach((field) => {
    (merged[field] as number) = (into[field] as number) + (from[field] as number);
  });
  // Per-unit rates are the same wherever a machine appears, so the first wins.
  return merged;
}

/** Machine rows summed across every priced spread in the project. */
export function projectMachineLines(
  project: ProjectState,
  states: Record<string, SpreadPriceState>
): MachineLine[] {
  const byKey = new Map<string, MachineLine>();
  project.spreads.forEach((spread) => {
    const state = states[spread.id];
    if (state?.status !== 'done') return;
    machineLines(state.result.welding.details).forEach((row) => {
      const existing = byKey.get(row.key);
      byKey.set(row.key, existing ? merge(existing, row) : row);
    });
  });
  return Array.from(byKey.values()).sort((a, b) => b.totalPrice - a.totalPrice);
}

/** Margin on a machine's rental revenue against its purchase cost, or null when unpriced. */
export function machineMargin(row: MachineLine): number | null {
  return row.totalPrice > 0 ? (row.totalPrice - row.hardwareCost) / row.totalPrice : null;
}
